import { gambits, localize } from '@/core/gambits';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import { openLiveHandshakeClock } from '@/views/main/clock/utils/open-handshake-clock';
import {
  handshakeCta,
  hasPlayedPosition,
  pickLiveHandshake,
  type HandshakeCta,
} from '@/views/main/handshakes/utils/handshake-game';
import { currentRecommendation, type CurrentRecommendation } from '@/views/main/handshakes/utils/recommendation-state';
import { useNow } from '@vueuse/core';
import { useToast } from 'vue-toastification';

interface TapPlace {
  locationId?: string | null;
  gambitId?: string | null;
}

/**
 * What a tap on a player does at a spot or gambit, read from the live store so
 * every list agrees with the profile. Open plays the game, reopen shakes hands
 * again and brings the closed game back, handshake falls through to the caller.
 */
export function useLiveHandshakeTap(place: () => TapPlace) {
  const store = useLiveHandshakesStore();
  const toast = useToast();
  const now = useNow({ interval: 60 * 1000 });

  void store.ensure();

  const picked = (peerId: string) =>
    pickLiveHandshake(store.withPeer(peerId), { locationId: place().locationId }, now.value);

  const cta = (peerId: string): HandshakeCta => handshakeCta(picked(peerId));

  /** The open game no one has played a position on yet. */
  const isUnplayed = (peerId: string): boolean => {
    const live = picked(peerId);
    return !!live && !live.closedAt && !hasPlayedPosition(live);
  };

  /** Where the recommendation in this player's open game stands, for the button that opens it. */
  const recommendation = (peerId: string): CurrentRecommendation => {
    const live = picked(peerId);
    return live && !live.closedAt ? currentRecommendation(live) : { state: 'unknown', position: null };
  };

  const reopen = async (peerId: string): Promise<boolean> => {
    const { locationId, gambitId } = place();
    const result = await gambits.userService.offerHandshake(
      peerId,
      undefined,
      gambitId ? { gambitId } : { locationId: locationId ?? undefined },
    );

    if (result.error || !result.data) {
      toast.error(result.error?.message || localize('handshake.reopen.error'));
      return true;
    }

    await store.load();
    await openLiveHandshakeClock(result.data.id, localize('handshake.player.passerby'));
    return true;
  };

  /** Handles the tap when a live handshake decides it. False leaves it to the caller. */
  const tap = async (peerId: string): Promise<boolean> => {
    await store.ensure();

    const live = picked(peerId);
    const action = handshakeCta(live);
    if (action === 'open') {
      await openLiveHandshakeClock(live!.id, localize('handshake.player.passerby'));
      return true;
    }
    if (action === 'reopen') return reopen(peerId);

    return false;
  };

  return { cta, isUnplayed, recommendation, tap };
}
